// How the host moves the enemy. The VM doesn't move host-spawned actors, so each tick the testbed
// works out where the enemy should be and sets it before vm.tick(). Every mode is a function of
// the tick, the seed and the inputs (track also follows the player), and the result goes into the
// per-tick input log, so rewind replays it exactly.
import { BOSS, FIELD } from './sim.ts';

export const modes = ['static', 'sway', 'orbit', 'figure-8', 'wander', 'track'] as const;
export type Mode = (typeof modes)[number];

export interface Point {
  x: number;
  y: number;
}

const TAU = Math.PI * 2;
/** Wander: ticks spent gliding to the next point, then waiting there. */
const GLIDE = 90;
const HOLD = 60;
/** Switching modes eases from where the enemy is over this many ticks instead of jumping. */
const BLEND = 45;
const TRACK_SPEED = 1.5;

/** Where `mode` puts the enemy at tick `t`. `prev` is last tick's position, which only track uses. */
function place(mode: Mode, t: number, seed: number, prev: Point, player: Point): Point {
  switch (mode) {
    case 'static':
      return { x: BOSS.x, y: BOSS.y };
    case 'sway':
      return { x: BOSS.x + reach(96) * Math.sin((TAU * t) / 240), y: BOSS.y };
    case 'orbit':
      return { x: BOSS.x + 36 * Math.cos((TAU * t) / 180), y: BOSS.y + 36 * Math.sin((TAU * t) / 180) };
    case 'figure-8':
      return { x: BOSS.x + reach(112) * Math.sin((TAU * t) / 360), y: BOSS.y + 28 * Math.sin((2 * TAU * t) / 360) };
    case 'wander': {
      const leg = Math.floor(t / (GLIDE + HOLD));
      const into = t - leg * (GLIDE + HOLD);
      const from = leg === 0 ? { x: BOSS.x, y: BOSS.y } : wanderPoint(seed, leg - 1);
      const to = wanderPoint(seed, leg);
      return lerp(from, to, smooth(Math.min(1, into / GLIDE)));
    }
    case 'track': {
      const dx = Math.max(-TRACK_SPEED, Math.min(TRACK_SPEED, (player.x - prev.x) * 0.04));
      return { x: clampX(prev.x + dx), y: prev.y + (BOSS.y - prev.y) * 0.05 };
    }
  }
}

/** Tracks the current mode and eases between modes when it changes. */
export class Mover {
  mode: Mode = 'static';
  private from: Point = { x: BOSS.x, y: BOSS.y };
  private since = 0;

  /** Changes mode at tick `t`, starting from wherever the enemy is now. */
  setMode(mode: Mode, t: number, at: Point): void {
    if (mode === this.mode) return;
    this.mode = mode;
    this.from = at;
    this.since = t;
  }

  /** Starts the current mode afresh at tick `t`, e.g. after a reset. */
  restart(t: number, at: Point): void {
    this.from = at;
    this.since = t;
  }

  position(t: number, seed: number, prev: Point, player: Point): Point {
    const p = place(this.mode, t, seed, prev, player);
    const k = (t - this.since) / BLEND;
    return k >= 1 || this.mode === 'track' ? p : lerp(this.from, p, smooth(Math.max(0, k)));
  }
}

/** A seeded point in the upper part of the field for wander leg `leg`. */
function wanderPoint(seed: number, leg: number): Point {
  return {
    x: 56 + hash01(seed, leg * 2) * (FIELD.w - 112),
    y: 296 + hash01(seed, leg * 2 + 1) * 112,
  };
}

/** A number in [0, 1) from (seed, n), the same on every run. */
function hash01(seed: number, n: number): number {
  let h = Math.imul(seed ^ 0x9e3779b9, 0x85ebca6b) ^ Math.imul(n + 0x632be5ab, 0xc2b2ae35);
  h = Math.imul(h ^ (h >>> 16), 0x7feb352d);
  h = Math.imul(h ^ (h >>> 15), 0x846ca68b);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

/** A sideways swing of up to `max`, narrowed so the enemy stays on a slim field. */
function reach(max: number): number {
  return Math.min(max, BOSS.x - 40);
}

function smooth(k: number): number {
  return k * k * (3 - 2 * k);
}

function lerp(a: Point, b: Point, k: number): Point {
  return { x: a.x + (b.x - a.x) * k, y: a.y + (b.y - a.y) * k };
}

function clampX(x: number): number {
  return Math.max(32, Math.min(FIELD.w - 32, x));
}
